<template>
  <div class="charts-container">
    <div v-if="modified1 && modified1.length > 0" class="chart-panel chart-panel-left">
      <div class="legend-list" :style="{ color: legendTextColor }">
        <div
          v-for="item in sortedModified1"
          :key="`stock-${item.symbol}`"
          class="legend-item"
          tabindex="0"
          @mouseenter="handleLegendHover(item)"
          @mouseleave="handleLegendLeave(item)"
          @focus="handleLegendHover(item)"
          @blur="handleLegendLeave(item)"
        >
          <span
            class="legend-marker"
            :style="{ backgroundColor: item.sliceColor }"
          ></span>
          <span
            class="legend-symbol"
            :title="`${item.name} (${formatLegendPercent(
              item.value,
              modified1Total,
            )})`"
          >
            <span class="legend-name">{{ item.name }}</span>
            <span class="legend-percentage">
              ({{ formatLegendPercent(item.value, modified1Total) }})
            </span>
          </span>
        </div>
      </div>
      <div class="chart-wrapper">
        <h3 class="chart-title">Хувьцаа</h3>
        <div ref="chartdiv1" class="chart-canvas"></div>
      </div>
    </div>
    <div v-if="modified2 && modified2.length > 0" class="chart-panel chart-panel-right">
      <div class="chart-wrapper">
        <h3 class="chart-title">Бонд</h3>
        <div ref="chartdiv2" class="chart-canvas"></div>
      </div>
      <div class="legend-list" :style="{ color: legendTextColor }">
        <div
          v-for="item in sortedModified2"
          :key="`bond-${item.symbol}`"
          class="legend-item"
          tabindex="0"
          @mouseenter="handleLegendHover(item)"
          @mouseleave="handleLegendLeave(item)"
          @focus="handleLegendHover(item)"
          @blur="handleLegendLeave(item)"
        >
          <span
            class="legend-marker"
            :style="{ backgroundColor: item.sliceColor }"
          ></span>
          <span
            class="legend-symbol"
            :title="`${item.name} (${formatLegendPercent(
              item.value,
              modified2Total,
            )})`"
          >
            <span class="legend-name">{{ item.name }}</span>
            <span class="legend-percentage">
              ({{ formatLegendPercent(item.value, modified2Total) }})
            </span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { mapState } from "vuex";
import api from "../../api/stoTrade";

am4core.useTheme(am4themes_animated);

const CHART_COLORS = [
  "#E63946",
  "#2196F3",
  "#4CAF50",
  "#FF9800",
  "#9C27B0",
  "#00BCD4",
  "#FFEB3B",
  "#FF4081",
  "#00E676",
  "#FF6D00",
  "#1A237E",
  "#76FF03",
];

export default {
  props: {
    data: {
      type: Array,
      default: () => [],
    },
  },
  async mounted() {
    await this.fetchBrands();
    await this.$nextTick();

    if (this.modified1 && this.modified1.length > 0) {
      this.chart1 = this.createChart(
        this.$refs.chartdiv1,
        this.modified1,
        true,
      );
    }
    if (this.modified2 && this.modified2.length > 0) {
      this.chart2 = this.createChart(
        this.$refs.chartdiv2,
        this.modified2,
        true,
      );
    }
  },

  beforeDestroy() {
    if (this.chart1) this.chart1.dispose();
    if (this.chart2) this.chart2.dispose();
  },

  data() {
    return {
      modified1: null,
      modified2: null,
      chart1: null,
      chart2: null,
    };
  },
  computed: {
    ...mapState("common", ["dark"]),
    legendTextColor() {
      return this.dark ? "#FFFFFF" : "#000000";
    },
    modified1Total() {
      return this.calculateLegendTotal(this.modified1);
    },
    modified2Total() {
      return this.calculateLegendTotal(this.modified2);
    },
    sortedModified1() {
      return this.sortLegendItems(this.modified1);
    },
    sortedModified2() {
      return this.sortLegendItems(this.modified2);
    },
  },
  methods: {
    async fetchBrands() {
      if (!Array.isArray(this.data) || this.data.length === 0) return;

      const processGroup = async (group, multiplier = 1) => {
        const prices = await Promise.all(
          group.map(async (element) => {
            if (!element.symbol) return [element.symbol, null];
            try {
              const { data } = await api.symbolOCHL(element.symbol);
              const closingPrice = Number(data && data.closing_price);
              return [
                element.symbol,
                Number.isFinite(closingPrice) ? closingPrice : null,
              ];
            } catch (error) {
              return [element.symbol, null];
            }
          }),
        );

        const priceMap = prices.reduce(
          (accumulator, [symbol, closingPrice]) => {
            accumulator[symbol] = closingPrice;
            return accumulator;
          },
          {},
        );

        return group
          .map((element, index) => {
            const closingPrice = priceMap[element.symbol];
            if (closingPrice === null || closingPrice === undefined)
              return null;

            const quantity = element.available
              ? element.available
              : element.value;

            return {
              ...element,
              sliceColor: CHART_COLORS[index % CHART_COLORS.length],
              value: Math.ceil(closingPrice * quantity * multiplier),
            };
          })
          .filter(Boolean);
      };

      const data1 = this.data.filter((el) => el.stype === 1);
      const data2 = this.data.filter((el) => el.stype === 2);

      if (data1.length > 0) this.modified1 = await processGroup(data1);
      if (data2.length > 0) this.modified2 = await processGroup(data2, 1000);
    },

    createChart(el, data, showCurrency = false) {
      const chart = am4core.create(el, am4charts.PieChart);
      chart.data = data;

      const pieSeries = chart.series.push(new am4charts.PieSeries());

      pieSeries.events.on("datavalidated", () => {
        pieSeries.dataItems.each((dataItem) => {
          if (!dataItem || !dataItem.dataContext) return;

          dataItem.dataContext._slice = dataItem.slice;
          dataItem.dataContext._dataItem = dataItem;
        });
      });

      pieSeries.slices.template.propertyFields.fill = "sliceColor";
      pieSeries.slices.template.propertyFields.stroke = "sliceColor";
      pieSeries.slices.template.strokeOpacity = 1;
      pieSeries.dataFields.value = "value";
      pieSeries.dataFields.category = "name";
      pieSeries.slices.template.tooltipText =
        "{symbol}\n{name}: {value.value} ₮";

      chart.innerRadius = am4core.percent(40);

      pieSeries.labels.template.disabled = true;
      pieSeries.ticks.template.disabled = true;

      if (showCurrency) {
        pieSeries.legendSettings.valueText = "{value.value} ₮";
      }

      return chart;
    },

    handleLegendHover(item) {
      if (!item || !item._slice) return;

      item._slice.isHover = true;
      item._slice.toFront();

      if (typeof item._slice.showTooltip === "function") {
        item._slice.showTooltip();
      }
    },

    handleLegendLeave(item) {
      if (!item || !item._slice) return;

      item._slice.isHover = false;

      if (typeof item._slice.hideTooltip === "function") {
        item._slice.hideTooltip();
      }
    },

    calculateLegendTotal(items) {
      if (!Array.isArray(items) || items.length === 0) return 0;

      return items.reduce((sum, item) => sum + Number(item.value || 0), 0);
    },

    formatLegendPercent(value, totalValue) {
      if (!totalValue) return "0.00%";

      return `${((Number(value || 0) / totalValue) * 100).toFixed(2)}%`;
    },

    sortLegendItems(items) {
      if (!Array.isArray(items) || items.length === 0) return [];

      const totalValue = this.calculateLegendTotal(items);

      return [...items].sort((left, right) => {
        const leftShare =
          totalValue > 0 ? Number(left.value || 0) / totalValue : 0;
        const rightShare =
          totalValue > 0 ? Number(right.value || 0) / totalValue : 0;

        if (rightShare !== leftShare) {
          return rightShare - leftShare;
        }

        return String(left.name || "").localeCompare(String(right.name || ""));
      });
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.charts-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Панел бүр өөрийн гадаргуутай болсноор хоорондох зай нь эцэг
   .auth-page-section-ийн өнгөөр тодорч, хоёр график тусдаа харагдана. */
.chart-panel {
  width: 100%;
  display: flex;
  gap: 24px;
  align-items: flex-start;
  padding: 24px;
  border-radius: 6px;
  background-color: var(--trading-grid-item-bg-color);
}

.chart-panel-left {
  flex-direction: row;
}

.chart-panel-right {
  flex-direction: row-reverse;
}

.chart-wrapper {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.chart-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
}

.chart-canvas {
  flex: 1 1 auto;
  min-width: 0;
  height: 420px;
}

.legend-list {
  flex: 0 0 280px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  font-size: 14px;
  line-height: 1.4;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.legend-item:hover,
.legend-item:focus {
  opacity: 0.8;
  outline: none;
}

.legend-marker {
  width: 12px;
  height: 12px;
  border-radius: 999px;
  flex: 0 0 12px;
}

.legend-symbol {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1 1 auto;
  font-weight: 600;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
}

.legend-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.legend-percentage {
  flex: 0 0 auto;
  font-weight: 500;
  opacity: 0.75;
  white-space: nowrap;
}

@media (max-width: 767px) {
  .chart-panel {
    flex-direction: column !important;
    padding: 16px;
  }

  .chart-canvas {
    height: 360px;
  }

  .legend-list {
    flex: 1 1 auto;
    width: 100%;
  }
}
</style>
